import { Component, OnDestroy, OnInit } from '@angular/core';
import { ICurrentTelephonyUser } from '../../../interfaces/telephony/telephony-user.interface';
import {
  EventService,
  JobsContactService,
  OrganisationsService,
  TelephonyService,
} from '../../../services';
import { OrganisationSelectorEventTypeEnum } from '../../../interfaces/telephony/organisation-selector-event-type.enum';
import { BroadcastHubMessage } from '../../../interfaces/broadcast-hub-message';
import { BroadcastHubService } from '../../../services/broadcast-hub.service';
import { BroadcastHubEventService } from '../../../services/broadcast-hub-event.service';
import { BroadcastHubMessageGroupEnum } from '../../../enums/broadcast-hub-message-group.enum';
import { Subscription } from 'rxjs';
import { TelephonyCallStateEnum } from '../../../enums/telephony-call-state.enum';
import { ITelephonyEvent, TelephonyEventEnum } from '../../../interfaces/telephony/telephony-event';
import { DialpadCallEventDataDTO } from '../../../interfaces/telephony/call-event-data.dto.interface';
import { ICallStateData } from '../../../interfaces/telephony/call-state.data';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ILogCallDTO } from '../../../interfaces/telephony/log-call.dto.interface';
import { TelephonyStatEnum } from '../../../interfaces/telephony/telephone-stat.enum';
import { Message } from '../../../classes/Message';
import { MessageService, SelectItem } from 'primeng';
import { OrganisationTypeEnum } from '../../../enums/organisation-types';
import { Router } from '@angular/router';
import { IOrganisationSelectorEvent } from '../../../interfaces/telephony/organisation-selector-event';
import moment from 'moment-timezone';
import { IJobContact } from '../../../interfaces/job-contact';
import { ILogCallTaskInfo } from '../../../interfaces/telephony/log-call-job-info';
import { datadogLogs } from '@datadog/browser-logs';
import { getCallStatusText } from './call-utils';

@Component({
  selector: 'app-telephony-control',
  templateUrl: './telephony-control.component.html',
  styleUrls: ['./telephony-control.component.scss'],
})
export class TelephonyControlComponent implements OnInit, OnDestroy {
  private logger = datadogLogs.createLogger('TelephonyControlComponent');

  constructor(
    private telephonyService: TelephonyService,
    private eventService: EventService,
    private awsAppSyncService: BroadcastHubService,
    private awsAppSyncEventService: BroadcastHubEventService,
    private orgService: OrganisationsService,
    private messageService: MessageService,
    private router: Router,
    private contactService: JobsContactService,
    fb: FormBuilder,
  ) {
    this.logForm = fb.group({
      taskId: new FormControl(null, Validators.required),
      note: new FormControl('', Validators.required),
      logEngineius: new FormControl(true),
      logSupplier: new FormControl(false),
      logCustomer: new FormControl(false),
      contactOutcomeId: new FormControl(null),
    });

    this.eventService.OrganisationSelectorEvent.subscribe((event: IOrganisationSelectorEvent) => {
      if (event.eventType == OrganisationSelectorEventTypeEnum.AddNumberSelectorClosed) {
        let user = event.args.selectedUser;
        let org = event.args.selectedOrg;

        if (org) {
          this.callStateData.callerInfo.organisationId = org.id;
          this.callStateData.callerInfo.organisationTypeId = org.organisationTypeId;
        }

        if (event.args.selectedUser) {
          this.callStateData.callerInfo.name = `${user.name} (${user.email})`;
        } else if (event.args.selectedOrg) {
          this.callStateData.callerInfo.name = org.name;
        }

        this.setActionText();
      }
    });
  }

  telephonyUser: ICurrentTelephonyUser;
  telephonyEventSub: Subscription;
  internalTelephonyEventSub: Subscription;

  public TelephonyCallState = TelephonyCallStateEnum;

  callStateData: ICallStateData;
  recentCalls: ICallStateData[] = [];
  selectedCall: ICallStateData = null;
  recentCallsSub: Subscription;

  // this data only changes when a new call is answered
  // it is used for logging a call since that may occur after the call has ended
  // contextCallState: ICallStateData;

  internalTelephonyEventData: ITelephonyEvent;
  showLogCallDialog = false;
  loggingFromDeployment = false;
  public outcomeOptions: SelectItem[] = []; // when logging from deployment

  selectedTask: ILogCallTaskInfo; // for saving call association to db for specific task

  public get state(): ICallStateData {
    return this.callStateData;
  }

  recentCallDropdownOptions: SelectItem[] = [];

  actionText: string = null; // context-based action button (i.e. open profile, add number)

  logForm: FormGroup;

  public get showCallStateControl(): boolean {
    return (
      this.telephonyUser &&
      this.callStateData &&
      this.callStateData?.callState != TelephonyCallStateEnum.None
    );
  }

  public get showDefaultControl(): boolean {
    return this.telephonyUser && !this.showCallStateControl;
  }

  async ngOnInit() {
    this.telephonyService.getCurrentTelephonyUser().then((res) => {
      this.telephonyUser = res;
      this.logger.info('Loaded telephony user', this.telephonyUser);

      if (this.telephonyUser) {
        this.initEventListener();
        this.initInternalEventListener();
      }
    });

    // if we have a call already in the service, set context call state
    this.callStateData = await this.telephonyService.GetCurrentCallState();
    if (this.callStateData) {
      this.logger.info('Found existing call state data', this.callStateData);

      await this.processCallState(this.callStateData.latestEvent);
    }

    this.recentCallsSub = this.telephonyService.RecentCalls$.subscribe((calls) => {
      this.recentCalls = calls;
      if (this.selectedCall) {
        const updated = calls.find(
          (c) => c.latestEvent?.call_id === this.selectedCall.latestEvent?.call_id,
        );
        if (updated) {
          this.selectedCall = updated;
        }
      }
      this.updateRecentCallDropdownOptions();
    });
  }

  initEventListener() {
    // subscribe to specific telephony channel for this user
    this.logger.info('Subscribing to telephony broadcast channel', this.telephonyUser);
    void this.awsAppSyncService.subscribeToChannel('Telephony-' + this.telephonyUser.id);

    this.telephonyEventSub = this.awsAppSyncEventService.TelephonyEvent.subscribe(
      (msg: BroadcastHubMessage<any>) => {
        this.logger.info('Telephony broadcast event received', msg);
        if (msg.messageGroup == BroadcastHubMessageGroupEnum.Telephony) {
          void this.handleTelephonyEvent(msg);
        }
      },
      (err) => {
        this.logger.error('Telephony broadcast error received', {}, err);
      },
    );
  }

  initInternalEventListener() {
    this.internalTelephonyEventSub = this.eventService.InternalTelephonyEvent.subscribe((event) => {
      this.logger.info('Internal telephony event received', event);
      if (event.eventType == TelephonyEventEnum.LogCall) {
        this.logger.info('Handling internal log call event', event);
        this.internalTelephonyEventData = event;

        if (this.internalTelephonyEventData.telephonyJobInfo?.tasks?.length == 1) {
          this.selectedTask = this.internalTelephonyEventData.telephonyJobInfo.tasks[0];
        } else {
          this.selectedTask = null;
        }

        this.logForm.controls.note.setValue(null);

        this.loggingFromDeployment = !!event.fromDeployment;

        if (this.loggingFromDeployment && this.outcomeOptions?.length == 0) {
          this.contactService.getAllOutcomes().then((res) => {
            this.outcomeOptions = [];
            res.forEach((x) => this.outcomeOptions.push({ label: x.outcomeName, value: x.id }));
          });
        }

        if (this.recentCalls && this.recentCalls.length > 0) {
          this.selectedCall = this.recentCalls[0];
        } else {
          this.selectedCall = this.callStateData;
        }
        this.updateRecentCallDropdownOptions();

        // show log call dialog
        this.showLogCallDialog = true;
        this.logForm.patchValue({ contactOutcomeId: null });

        // focus on input
        setTimeout(() => {
          let element = document.getElementById('noteInput');
          if (element) {
            element.focus();
          }
        }, 150);
      }
    });
  }

  ngOnDestroy() {
    if (this.telephonyEventSub) {
      this.telephonyEventSub.unsubscribe();
    }
    if (this.internalTelephonyEventSub) {
      this.internalTelephonyEventSub.unsubscribe();
    }
    if (this.recentCallsSub) {
      this.recentCallsSub.unsubscribe();
    }
  }

  openAddNumberSelector() {
    this.eventService.OrganisationSelectorEvent.emit({
      eventType: OrganisationSelectorEventTypeEnum.OpenAddNumberSelector,
      args: true,
    });
  }

  public get userDisplayStatus(): string {
    if (!this.telephonyUser) return '';

    return this.telephonyUser.is_online ? 'Online' : 'Offline';
  }

  private async handleTelephonyEvent(msg: BroadcastHubMessage<any>) {
    this.logger.info('Handling telephony broadcast event', msg);
    let latestEvent = msg.messageData as DialpadCallEventDataDTO;
    await this.processCallState(latestEvent);
  }

  private async processCallState(latestEvent: DialpadCallEventDataDTO) {
    this.logger.info('Processing call state', latestEvent);
    this.actionText = null;

    this.callStateData = {
      latestEvent: latestEvent,
      callState: this.getCallState(latestEvent),
      callStateIcon: 'assets/images/telephony/',
      callerInfo: null,
      callStateText: '',
    };

    this.setCallStateIcon(latestEvent);

    // if we are 'in call' attempt to find the number
    if (this.callStateData.callState != TelephonyCallStateEnum.None) {
      if (latestEvent.contact.phone && latestEvent.contact.phone != '') {
        this.callStateData.callerInfo = await this.orgService.getCallerInfo(
          latestEvent.contact.phone,
        );
      }
    }

    if (!this.callStateData.callerInfo) {
      this.callStateData.callerInfo = {
        name: 'Anonymous',
        phoneNumber: latestEvent.contact.phone,
      };
    }

    this.callStateData.callerInfo.phoneNumber ||= latestEvent.contact.phone;
    this.setActionText();

    if (this.callStateData.callState == TelephonyCallStateEnum.Ended) {
      // record call info in database
      this.createCallTimeStat();
    }

    this.telephonyService.SetCurrentCallState(this.callStateData);
  }

  private setCallStateIcon(latestEvent: DialpadCallEventDataDTO) {
    if (latestEvent.direction == TelephonyService.EventState_Direction_Incoming) {
      this.callStateData.callStateIcon += 'incoming-call';
      this.callStateData.callStateText = 'Inbound';
    }
    if (latestEvent.direction == TelephonyService.EventState_Direction_Outgoing) {
      this.callStateData.callStateIcon += 'outgoing-call';
      this.callStateData.callStateText = 'Outbound';
    }

    if (this.callStateData.callState == TelephonyCallStateEnum.Answered) {
      this.callStateData.callStateIcon += '-green';
      this.callStateData.callStateText += ' in call';
    }
    if (this.callStateData.callState == TelephonyCallStateEnum.Ringing) {
      this.callStateData.callStateIcon += '-grey';
      this.callStateData.callStateText += ' ringing';
    }
    if (this.callStateData.callState == TelephonyCallStateEnum.Ended) {
      this.callStateData.callStateIcon += '-red';
      this.callStateData.callStateText += ' ended';
    }
    if (this.callStateData.callState == TelephonyCallStateEnum.OnHold) {
      this.callStateData.callStateIcon += '-grey';
      this.callStateData.callStateText += ' on hold';
    }

    this.callStateData.callStateIcon += '.svg';
  }

  private setActionText() {
    if (
      this.callStateData.callState == TelephonyCallStateEnum.Answered &&
      (this.callStateData.callerInfo.phoneNumber || '') != ''
    ) {
      this.actionText = null;
      if (
        !this.callStateData.callerInfo.organisationId &&
        !this.callStateData.callerInfo.isDelisted
      ) {
        this.actionText = 'Add Number';
      } else if (!this.callStateData.callerInfo.isDelisted) {
        this.actionText = 'Open Profile';
      }
    }
  }

  updateRecentCallDropdownOptions() {
    const calls =
      this.recentCalls && this.recentCalls.length > 0
        ? this.recentCalls
        : this.selectedCall
        ? [this.selectedCall]
        : [];
    this.recentCallDropdownOptions = calls.map((c) => ({
      label: c.callerInfo?.name || 'Anonymous',
      value: c,
    }));
  }

  getCallStatusText(): string {
    return getCallStatusText(this.callStateData);
  }

  getCallState(event: DialpadCallEventDataDTO): TelephonyCallStateEnum {
    console.log('STATE: ', event.state);

    if (
      event.state == TelephonyService.EventState_Ringing ||
      event.state == TelephonyService.EventState_Calling
    ) {
      return TelephonyCallStateEnum.Ringing;
    }
    if (event.state == TelephonyService.EventState_InCall) {
      return TelephonyCallStateEnum.Answered;
    }
    if (event.state == TelephonyService.EventState_EndCall) {
      return TelephonyCallStateEnum.Ended;
    }
    if (event.state == TelephonyService.EventState_OnHold) {
      return TelephonyCallStateEnum.OnHold;
    }

    return TelephonyCallStateEnum.None;
  }

  logCall() {
    // call can't be logged without a contextState
    this.logger.info('Attempting to log call', this.selectedCall);

    if (!this.selectedCall) {
      this.logger.warn('Could not log call as there is no valid call state data');
      return;
    }

    this.logForm.patchValue({ taskId: this.selectedTask?.id });

    if (!this.logForm.valid) {
      this.logger.info('Log call form validation failed');
      return;
    }

    // save call log in job history
    let dto: ILogCallDTO = {
      callId: this.selectedCall.latestEvent.call_id,
      ...this.logForm.value,
      jobId: this.selectedTask.jobId,
      operatorId: this.selectedCall.latestEvent.target.id,
      callerName: this.selectedCall.callerInfo.name,
      callStarted: this.selectedCall.latestEvent.date_started,
      outbound:
        this.selectedCall.latestEvent.direction == TelephonyService.EventState_Direction_Outgoing,
    };

    this.logger.info('Sending log call request', dto);

    this.telephonyService
      .logCall(dto)
      .then(() => {
        // raise event so screen can refresh notes etc.
        this.eventService.InternalTelephonyEvent.emit({ eventType: TelephonyEventEnum.CallLogged });

        // refresh notes and show message
        this.messageService.add(Message.Success('Call logged.', 'Info'));
      })
      .catch((error) => {
        this.logger.error('Failed to log call', dto, error);
        this.messageService.add(Message.Error('Failed to log call', 'Error'));
      });

    if (this.loggingFromDeployment) {
      if (!!this.selectedCall.callerInfo.organisationId) {
        // log the job contact
        const outcomeId = this.logForm.get('contactOutcomeId').value;

        let dto = {
          id: 0,
          jobId: this.selectedTask.jobId,
          contactOutcomeId: outcomeId,
          supplierId: this.selectedCall.callerInfo.organisationId,
          contactMethodId: 1,
        } as IJobContact;
        void this.contactService.create(dto);
      } else {
        this.messageService.add(Message.Error('Contact history not logged', 'Error'));
      }
    }
    this.showLogCallDialog = false;
  }

  createCallTimeStat() {
    let callState = this.callStateData;

    if (callState.latestEvent.call_stat_created) {
      return;
    }

    // console.log('Context state: ', this.contextCallState);
    // console.log('Call state data: ', this.callStateData);
    // console.log('State: ', callState);

    let statEnum =
      callState?.latestEvent?.call_id != this.callStateData?.latestEvent?.call_id
        ? TelephonyStatEnum.Missed
        : TelephonyStatEnum.Answered;

    let startDate = callState.latestEvent?.date_started
      ? moment.tz(callState.latestEvent.date_started, 'Europe/London').toDate()
      : moment().toDate();

    console.log(
      'START DATE: ',
      moment(startDate).tz('Europe/London').format('YYYY-MM-DD HH:mm:ss z'),
    );
    console.log('call start: ', callState.latestEvent?.date_started);

    const callTime = this.getCallTime(startDate);

    let stat = {
      Created: startDate,
      OperatorName: `${this.telephonyUser?.first_name} ${this.telephonyUser?.last_name}`,
      Extension: this.telephonyUser?.extension,
      CallingPhone: callState.callerInfo?.phoneNumber,
      OutboundCall:
        callState.latestEvent.direction == TelephonyService.EventState_Direction_Outgoing,
      StatEnum: statEnum,
      CallingOrganisationId: callState.callerInfo?.organisationId,
      Duration: callTime,
      WrapUpCallReasonId: null,
      callingOrganisationName: callState.callerInfo?.name,
      callId: callState.latestEvent.call_id,
    };

    this.telephonyService.createTelephonyStat(stat).then();
  }

  getCallTime(startDate: Date): number {
    const timeZone = 'Europe/London';

    const start = moment(startDate).tz(timeZone);
    const now = moment().tz(timeZone);

    return now.diff(start, 'seconds');
  }

  onAction() {
    let callState = this.callStateData;

    console.log('Caller INFO: ', callState.callerInfo);

    if (callState.callState == TelephonyCallStateEnum.Ended) {
      this.clear();
      return;
    }

    if (!callState?.callerInfo?.organisationId) {
      this.openAddNumberSelector();
    } else {
      // navigate to supplier profile
      if (callState?.callerInfo?.organisationTypeId == OrganisationTypeEnum.Supplier) {
        // navigate to stand-alone supplier info page
        void this.router.navigate([
          `app/supplier-information/${callState?.callerInfo?.organisationId}`,
        ]);
      }

      // navigate to customer profile
      if (callState?.callerInfo?.organisationTypeId == OrganisationTypeEnum.Customer) {
        // navigate to stand-alone supplier info page
        void this.router.navigate([
          `app/customer-information/${callState?.callerInfo?.organisationId}`,
        ]);
      }
    }
  }

  protected clear() {
    this.telephonyService.SetCurrentCallState(null);
    this.callStateData = null;
    this.selectedCall = null;
  }
}
